import { environment } from '@env/environment';
import { Inject, Injectable, DOCUMENT } from '@angular/core';


const VERSION = '1.2.1';

@Injectable()
export class GhStorageService {
  private model: any;
  private storageEnabled = true;

  constructor(
    @Inject(DOCUMENT) private document: any,
  ) {
    this.storageEnabled = this.init();
  }

  init(): boolean {
    this.model = {};
    try {
      const meta = this.get('META') || {};
      if (meta.version && meta.version !== VERSION) {
        localStorage.clear();
      }
      meta.version = VERSION;
      this.set('META', meta);
    } catch (e) {
      return false;
    }
    return true;
  }

  // no managed for disabled storage
  clear() {
    this.model = {};
    localStorage.clear();
  }

  // no managed for disabled storage
  remove(key: string) {
    delete this.model[key];
  }

  set(key: string, value: any) {
    this.model[key] = value;
    if (this.storageEnabled) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        console.error(`No able to store the required value in the localStore:${e}`);
      }
    } else {
      this.setCookie(key, JSON.stringify(value));
    }
  }

  get(key: string) {
    if (this.model[key] === undefined && this.storageEnabled) {
      const rawData = localStorage.getItem(key);
      if (rawData === '[object Object]') {
        throw new Error('Corrupted localStorage data for key: ' + key);
      }
      this.model[key] = JSON.parse(rawData || null);
    }
    return this.model[key];
  }

  safeGet(key: string) {
    return this.storageEnabled ? this.get(key) : this.getCookie(key);
  }

  // Mozilla Cookie Framework
  // https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie/Simple_document.cookie_framework
  setCookie(key: string, value: any, end?: any, path?: string): boolean {
    if (!key || /^(?:expires|max\-age|path|domain|secure)$/i.test(key)) {
      return false;
    }
    const cfgDomain = environment.domain;
    const domain = cfgDomain === 'localhost' ? cfgDomain : `.${cfgDomain}`;
    let expires = '';

    if (end && end !== 0) { // reserve 0 for clear
      switch (end.constructor) {
        case Number:
          expires = end === Infinity ? '; expires=Fri, 31 Dec 9999 23:59:59 GMT' : '; max-age=' + end;
          break;
        case String:
          expires = '; expires=' + end;
          break;
        case Date:
          expires = '; expires=' + end.toUTCString();
          break;
      }
    }

    value = typeof value === 'object' ? JSON.stringify(value) : value;
    this.document.cookie = encodeURIComponent(key) + '='
      + encodeURIComponent(value)
      + expires
      + (path ? '; path=' + path : `;domain=${domain};path=/`);
    return true;
  }

  getCookie(sKey: string) {
    if (!sKey) {
      return null;
    }
    return decodeURIComponent(this.document.cookie.replace(
      new RegExp('(?:(?:^|.*;)\\s*' + encodeURIComponent(sKey).replace(/[\-\.\+\*]/g, '\\$&') + '\\s*\\=\\s*([^;]*).*$)|^.*$'), '$1')
    ) || null;
  }

  removeCookie(sKey: string, sPath = '', sDomain = '') {
    if (!this.hasItem(sKey)) {
      return false;
    }
    const cfgDomain = environment.domain;
    const domain = cfgDomain === 'localhost' ? cfgDomain : `.${cfgDomain}`;
    this.document.cookie = encodeURIComponent(sKey) + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT' +
      ('; domain=' + (sDomain ? sDomain : domain)) + ('; path=' + (sPath ? sPath : '/'));
    return true;
  }

  hasItem(ckey) {
    if (!ckey || /^(?:expires|max\-age|path|domain|secure)$/i.test(ckey)) {
      return false;
    }
    return (new RegExp('(?:^|;\\s*)' + encodeURIComponent(ckey).replace(/[\-\.\+\*]/g, '\\$&') + '\\s*\\=')).test(this.document.cookie);
  }
}
